import React, { useState, useEffect } from 'react';
import { updateTradeInDB } from '../journaling/JournalingService';
import { X } from 'lucide-react';
import { trackTradeEdited } from '../../services/analytics';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { getTradeableAssets } from '../../services/tradeableAssetsService';
import { getAssetParams, getQuantityLabel, calculatePips, calculateProfit } from '../../utils/tradeCalculations';
import ScreenshotUpload from '../../components/ui/ScreenshotUpload';
import { getTradeScreenshots } from '../../services/screenshotService';
import { useSubscription } from '../../hooks/useSubscription';

const EditTradeModal = ({ trade, onClose, onUpdate }) => {
  const [assets, setAssets] = useState([]);
  const [screenshots, setScreenshots] = useState([]);
  const { planId } = useSubscription();

  useEffect(() => {
    getTradeableAssets().then(setAssets).catch(console.error);
    if (trade?.id) {
      getTradeScreenshots(trade.id).then(setScreenshots).catch(console.error);
    }
  }, [trade?.id]);

  // Helper function to format date for datetime-local input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      // Handle both ISO strings and timestamps
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      // Format to YYYY-MM-DDThh:mm
      return date.toISOString().slice(0, 16);
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Helper function to format date for API
  const formatDateForAPI = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return date.toISOString();
    } catch (error) {
      console.error('Error formatting date for API:', error);
      return null;
    }
  };

  // Initialize state with formatted dates
  const [editedTrade, setEditedTrade] = useState(() => {
    // Format dates (DB returns lowercase: opentime, closetime)
    const formattedDates = {
      trade_date: formatDateForInput(trade.trade_date),
      openTime: formatDateForInput(trade.openTime || trade.opentime),
      closeTime: formatDateForInput(trade.closeTime || trade.closetime)
    };

    return {
      ...trade,
      ...formattedDates,
      orderType: trade.orderType || 'market',
      commission: trade.commission ?? 0,
      swap: trade.swap ?? 0,
      analysis: {
        strategy: trade.analysis?.strategy || 50,
        risk: trade.analysis?.risk || 50,
        emotion: trade.analysis?.emotion || 50,
        notes: trade.analysis?.notes || ''
      }
    };
  });

  // Get asset params for the selected symbol
  const assetParams = getAssetParams(editedTrade.symbol, assets);
  const quantityLabel = assetParams ? getQuantityLabel(assetParams.category) : 'Quantity';
  const isAutoCalc = !!assetParams;

  // Compute pips and profit directly during render (no hooks needed)
  let autoPips = null;
  let autoProfit = null;
  if (isAutoCalc) {
    autoPips = calculatePips({
      entryPrice: editedTrade.entry_price,
      exitPrice: editedTrade.exit_price,
      direction: editedTrade.direction,
      pipSize: assetParams.pipSize,
    });
    autoProfit = calculateProfit({
      entryPrice: editedTrade.entry_price,
      exitPrice: editedTrade.exit_price,
      direction: editedTrade.direction,
      quantity: editedTrade.lot_size,
      contractSize: assetParams.contractSize,
    });
  }

  const displayPips = isAutoCalc ? (autoPips ?? '') : editedTrade.pips;
  const displayProfit = isAutoCalc ? (autoProfit ?? '') : editedTrade.profit;

  // Debug: remove this console.log once auto-calc is confirmed working
  console.log('[EditTrade AutoCalc]', { isAutoCalc, symbol: editedTrade.symbol, entry: editedTrade.entry_price, exit: editedTrade.exit_price, direction: editedTrade.direction, lotSize: editedTrade.lot_size, pipSize: assetParams?.pipSize, contractSize: assetParams?.contractSize, autoPips, autoProfit, displayPips, displayProfit, assetsLoaded: assets.length });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setEditedTrade(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setEditedTrade(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSliderChange = (e) => {
    const { name, value } = e.target;
    const [parent, child] = name.split('.');
    setEditedTrade(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [child]: parseInt(value)
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Create the complete trade object
      const completeTrade = {
        ...editedTrade,
        entry_price: parseFloat(editedTrade.entry_price),
        exit_price: parseFloat(editedTrade.exit_price),
        lot_size: parseFloat(editedTrade.lot_size),
        pips: parseFloat(isAutoCalc ? autoPips : editedTrade.pips),
        profit: parseFloat(isAutoCalc ? autoProfit : editedTrade.profit),
        // Convert dates to ISO string
        openTime: formatDateForAPI(editedTrade.openTime),
        closeTime: formatDateForAPI(editedTrade.closeTime),
        trade_date: formatDateForAPI(editedTrade.trade_date)
      };

      // Update in DB
      await updateTradeInDB(editedTrade.id, completeTrade);
      trackTradeEdited({ symbol: completeTrade.symbol });

      // Notify parent component
      onUpdate(completeTrade);

      // Close modal
      onClose();
    } catch (error) {
      console.error('Error updating trade:', error);
      alert(`Failed to update trade: ${error.message}`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      {/* Modal */}
      <div className="relative z-50 w-full max-w-lg mx-4 flex flex-col max-h-[calc(100dvh-2rem)]">
        <div className="bg-[#1A2544] border border-white/[0.08] rounded-xl shadow-modal flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-border flex-shrink-0">
            <h2 className="text-lg md:text-xl font-bold text-foreground">Edit Trade</h2>
            <Button variant="ghost" size="icon" onClick={onClose} className="hover:bg-accent">
              <X className="h-4 w-4" />
            </Button>
          </div>
          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-4 md:p-6 space-y-4 md:space-y-6 overflow-y-auto flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* Symbol */}
              <div className="space-y-2">
                <Label htmlFor="symbol">Symbol</Label>
                <Input
                  id="symbol"
                  value={editedTrade.symbol}
                  onChange={e => handleChange({ target: { name: 'symbol', value: e.target.value } })}
                  placeholder="e.g., EURUSD"
                  className="bg-muted border-border"
                  required
                />
              </div>
              {/* Direction */}
              <div className="space-y-2">
                <Label htmlFor="direction">Direction</Label>
                <Select
                  value={editedTrade.direction}
                  onValueChange={value => handleChange({ target: { name: 'direction', value } })}
                >
                  <SelectTrigger className="bg-muted border-border">
                    <SelectValue placeholder="Select direction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BUY" className="text-green-500">BUY</SelectItem>
                    <SelectItem value="SELL" className="text-red-500">SELL</SelectItem>
                    <SelectItem value="LONG" className="text-green-500">LONG</SelectItem>
                    <SelectItem value="SHORT" className="text-red-500">SHORT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Entry Price */}
              <div className="space-y-2">
                <Label htmlFor="entry_price">Entry Price</Label>
                <Input
                  id="entry_price"
                  type="number"
                  step="0.00001"
                  value={editedTrade.entry_price}
                  onChange={e => handleChange({ target: { name: 'entry_price', value: e.target.value } })}
                  className="bg-muted border-border"
                  required
                />
              </div>
              {/* Exit Price */}
              <div className="space-y-2">
                <Label htmlFor="exit_price">Exit Price</Label>
                <Input
                  id="exit_price"
                  type="number"
                  step="0.00001"
                  value={editedTrade.exit_price}
                  onChange={e => handleChange({ target: { name: 'exit_price', value: e.target.value } })}
                  className="bg-muted border-border"
                  required
                />
              </div>
              {/* Quantity (dynamic label) */}
              <div className="space-y-2">
                <Label htmlFor="lot_size">{quantityLabel}</Label>
                <Input
                  id="lot_size"
                  type="number"
                  step="0.01"
                  value={editedTrade.lot_size}
                  onChange={e => handleChange({ target: { name: 'lot_size', value: e.target.value } })}
                  className="bg-muted border-border"
                  required
                />
              </div>
              {/* Profit (auto-calculated) */}
              <div className="space-y-2">
                <Label htmlFor="profit">
                  Profit/Loss {isAutoCalc && <span className="text-xs text-muted-foreground">(auto)</span>}
                </Label>
                <Input
                  id="profit"
                  type="number"
                  step="0.01"
                  value={displayProfit}
                  onChange={isAutoCalc ? undefined : (e => handleChange({ target: { name: 'profit', value: e.target.value } }))}
                  readOnly={isAutoCalc}
                  className={isAutoCalc ? "bg-muted/50 border-border cursor-not-allowed" : "bg-muted border-border"}
                  tabIndex={isAutoCalc ? -1 : undefined}
                />
              </div>
              {/* Pips (auto-calculated) */}
              <div className="space-y-2">
                <Label htmlFor="pips">
                  Pips {isAutoCalc && <span className="text-xs text-muted-foreground">(auto)</span>}
                </Label>
                <Input
                  id="pips"
                  type="number"
                  step="0.1"
                  value={displayPips}
                  onChange={isAutoCalc ? undefined : (e => handleChange({ target: { name: 'pips', value: e.target.value } }))}
                  readOnly={isAutoCalc}
                  className={isAutoCalc ? "bg-muted/50 border-border cursor-not-allowed" : "bg-muted border-border"}
                  tabIndex={isAutoCalc ? -1 : undefined}
                />
              </div>
              {/* Date */}
              <div className="space-y-2">
                <Label htmlFor="trade_date">Date</Label>
                <Input
                  id="trade_date"
                  type="date"
                  value={editedTrade.trade_date ? editedTrade.trade_date.slice(0, 10) : ''}
                  onChange={e => handleChange({ target: { name: 'trade_date', value: e.target.value } })}
                  className="bg-muted border-border"
                  required
                />
              </div>
              {/* Entry Date/Time */}
              <div className="space-y-2">
                <Label htmlFor="openTime">Entry Date/Time</Label>
                <Input
                  id="openTime"
                  type="datetime-local"
                  value={editedTrade.openTime ? editedTrade.openTime.slice(0, 16) : ''}
                  onChange={e => handleChange({ target: { name: 'openTime', value: e.target.value } })}
                  className="bg-muted border-border"
                  required
                />
              </div>
              {/* Exit Date/Time */}
              <div className="space-y-2">
                <Label htmlFor="closeTime">Exit Date/Time</Label>
                <Input
                  id="closeTime"
                  type="datetime-local"
                  value={editedTrade.closeTime ? editedTrade.closeTime.slice(0, 16) : ''}
                  onChange={e => handleChange({ target: { name: 'closeTime', value: e.target.value } })}
                  className="bg-muted border-border"
                  required
                />
              </div>
            </div>
            {/* Commission & Swap */}
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <div className="space-y-2">
                <Label htmlFor="commission">Commission <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Input
                  id="commission"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={editedTrade.commission}
                  onChange={e => handleChange({ target: { name: 'commission', value: e.target.value } })}
                  className="bg-muted border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="swap">Swap <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Input
                  id="swap"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={editedTrade.swap}
                  onChange={e => handleChange({ target: { name: 'swap', value: e.target.value } })}
                  className="bg-muted border-border"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1.5 md:space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                value={editedTrade.notes}
                onChange={e => handleChange({ target: { name: 'notes', value: e.target.value } })}
                className="w-full min-h-[80px] md:min-h-[100px] p-3 rounded-md bg-muted border border-border text-foreground"
                placeholder="Add any additional notes about the trade..."
              />
            </div>
          </div>
          {/* Screenshots section */}
          <div className="px-4 md:px-6 pb-4 border-t border-border pt-4 flex-shrink-0">
            <ScreenshotUpload
              tradeId={trade.id}
              screenshots={screenshots}
              planId={planId}
              onScreenshotsChange={setScreenshots}
            />
          </div>

          {/* Sticky Footer Buttons */}
          <div className="flex justify-end gap-4 p-4 border-t border-border flex-shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="border-border hover:bg-accent"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditTradeModal;
